/**
 * Cinematic landing — pure CSS 3D + JS, dialed up.
 *
 * Beats (more dramatic):
 *  0  Chaos: 110 word-particles at random rotations, parallax depth,
 *     orbital drift, background nebula slowly rotating
 *  1  Focus: words yank toward centre, screen-rumble pulse,
 *     focus question types itself character by character with cursor
 *  2  Tree: a shockwave ripples out, connectors DRAW from centre,
 *     halos burst, hue glow flash
 *  3  Panel: scene cleared, editorial UI panel rises with CTA
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useScrollProgress, sub, ease, beat } from "./useScroll";
import LeadGate from "./LeadGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${BACKEND_URL}/api/download/decision-filter`;

const CHAOS_WORDS = [
  "should we?", "is it too late?", "what if I'm wrong?", "wait or move?",
  "what's the cost?", "do I have time?", "is this the right call?",
  "can we undo it?", "will it work?", "should I trust it?",
  "what am I missing?", "is now the moment?", "will I regret this?",
  "what's the worst case?", "am I delaying?", "am I rushing?",
  "is this clear?", "what really matters?", "what's the upside?",
  "what's the downside?", "should I wait?", "should I commit?",
  "is the timing right?", "do I know enough?",
  "what does success look like?", "what if I'm late?",
  "should I ask?", "should I jump?", "is it reversible?",
  "can I sleep on it?", "what's the real question?",
  "does it have to be now?", "is this fear talking?",
  "is this pressure?", "is this clarity?",
  "what would calm me say?", "what would future me say?",
  "what's true?", "what comes next?", "what breaks if I'm wrong?",
];

const ANCHORS = [
  { word: "Clarity",       angle: -135 },
  { word: "Consequence",   angle:  -45 },
  { word: "Reversibility", angle:  135 },
  { word: "Timing",        angle:   45 },
];

const FOCUS_TEXT = "What are you actually deciding?";

const rand = (a, b) => Math.random() * (b - a) + a;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const ProgressDots = ({ progress }) => {
  const b = beat(progress);
  return (
    <div className="cn__progress" data-testid="cinematic-progress">
      {ANCHORS.map((_, i) => (
        <div
          key={i}
          className={`cn__dot${b === i ? " cn__dot--active" : ""}`}
          data-testid={`cinematic-dot-${i + 1}`}
        >
          <span className="cn__dot-num">0{i + 1}</span>
          <span className="cn__dot-bar" />
        </div>
      ))}
    </div>
  );
};

const ScrollHint = ({ progress }) => {
  const op = (1 - ease(sub(progress, 0, 0.18))) * 0.85;
  if (op <= 0.02) return null;
  return (
    <div className="cn__hint" style={{ opacity: op }} data-testid="scroll-hint">
      <span className="cn__hint-eyebrow">SCROLL</span>
      <span className="cn__hint-line" />
    </div>
  );
};

const TopBar = ({ progress }) => {
  const op = 1 - ease(sub(progress, 0.05, 0.3)) * 0.6 + ease(sub(progress, 0.85, 1)) * 0.6;
  return (
    <div className="cn__topbar" style={{ opacity: Math.min(op, 1) }} data-testid="cinematic-topbar">
      <span className="cn__brand">Decision Engine</span>
      <span className="cn__brand-sub">VOL. 01  ·  BY HAPPY PEPPERS</span>
    </div>
  );
};

const CtaPanel = ({ progress, onCtaClick }) => {
  const op = ease(sub(progress, 0.85, 1));
  if (op <= 0) return null;
  const handle = (e) => {
    e.preventDefault();
    onCtaClick();
  };
  return (
    <div
      className="cn__panel"
      style={{ opacity: op, transform: `translate(-50%, -50%) translateY(${(1 - op) * 24}px)` }}
      data-testid="cta-panel"
    >
      <span className="cn__eyebrow">DECISION ENGINE  ·  BY HAPPY PEPPERS</span>
      <span className="cn__eyebrow-rule" />
      <h2 className="cn__title" data-testid="cta-title">A frame for thinking.</h2>
      <p className="cn__deck" data-testid="cta-deck">
        Run any important decision through four layers — Clarity,
        Consequence, Reversibility, Timing.
      </p>
      <a
        href={PDF_URL}
        onClick={handle}
        className="cn__cta"
        data-testid="cta-button"
      >
        <span>Download the free 7-page checklist</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14" /><path d="m13 5 7 7-7 7" />
        </svg>
      </a>
      <span className="cn__cta-meta">PDF · 7 pages · 124 KB · 48 fillable fields</span>
    </div>
  );
};

export default function Cinematic() {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const nebulaRef = useRef(null);
  const focusRef = useRef(null);
  const focusCaretRef = useRef(null);
  const burstRef = useRef(null);
  const rumbleRef = useRef(null);
  const progress = useScrollProgress(wrapRef);
  const [gateOpen, setGateOpen] = useState(false);

  // 110 chaos particles with random rotation + wider depth range
  const chaos = useMemo(
    () =>
      Array.from({ length: 110 }).map((_, i) => ({
        word: CHAOS_WORDS[i % CHAOS_WORDS.length],
        x: rand(-55, 55),
        y: rand(-50, 50),
        z: rand(-1200, 500),
        size: rand(12, 46),
        rot: rand(-18, 18),
        accent: Math.random() < 0.1,
        white:  Math.random() < 0.05,
        phase: rand(0, Math.PI * 2),
        spinSpeed: rand(-0.8, 0.8),
      })),
    []
  );

  const wordRefs = useRef([]);
  const anchorRefs = useRef([]);
  const linesRef = useRef([]);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = () => {
      const t = (performance.now() - start) / 1000;
      const p = progress;

      // ---- Background nebula slow rotate + scale ----
      if (nebulaRef.current) {
        const rot = t * 4 + p * 60;
        const scale = 1 + Math.sin(t * 0.4) * 0.08 + p * 0.2;
        nebulaRef.current.style.transform =
          `translate(-50%, -50%) rotate(${rot}deg) scale(${scale.toFixed(3)})`;
      }

      // ---- Camera dolly + tilt + spin during chaos + roll ----
      if (stageRef.current) {
        const dolly = -ease(sub(p, 0, 0.5)) * 260 - ease(sub(p, 0.5, 0.85)) * 160;
        const tiltX = ease(sub(p, 0, 0.5)) * 6 - ease(sub(p, 0.5, 0.85)) * 6;
        const tiltZ = (1 - ease(sub(p, 0, 0.55))) * Math.sin(t * 0.45) * 1.4;
        const slowSpin = (1 - ease(sub(p, 0, 0.5))) * Math.sin(t * 0.18) * 1.2;
        stageRef.current.style.transform =
          `translateZ(${dolly}px) rotateX(${tiltX}deg) rotateY(${slowSpin}deg) rotateZ(${tiltZ}deg)`;
      }

      // ---- Rumble pulse on Beat 1 climax (focus appearing) ----
      if (rumbleRef.current) {
        const rumbleStrength = ease(sub(p, 0.36, 0.46)) * (1 - ease(sub(p, 0.46, 0.55)));
        const rx = (Math.random() - 0.5) * rumbleStrength * 6;
        const ry = (Math.random() - 0.5) * rumbleStrength * 6;
        rumbleRef.current.style.transform = `translate(${rx}px, ${ry}px)`;
      }

      // ---- Chaos particles ----
      const fadeChaos = 1 - sub(p, 0.45, 0.7);
      const pull = ease(sub(p, 0.05, 0.5));
      const expand = ease(sub(p, 0.0, 0.18));   // quick outward push at very start
      chaos.forEach((d, i) => {
        const el = wordRefs.current[i];
        if (!el) return;
        const driftX = Math.sin(t * 0.4 + d.phase) * 5;
        const driftY = Math.cos(t * 0.35 + d.phase) * 4;
        const driftZ = Math.sin(t * 0.25 + d.phase) * 50;
        // outward push then pull-in (creates a "breathing" feel)
        const ox = d.x * (1 + expand * 0.25);
        const oy = d.y * (1 + expand * 0.25);
        const x = ox + driftX - (ox + driftX) * pull * 0.95;
        const y = oy + driftY - (oy + driftY) * pull * 0.95;
        const z = d.z + driftZ + (-150 - d.z) * pull * 0.7;
        // depth-based scale
        const depthScale = Math.max(0.4, 1 + d.z / 1500);
        // rotation continuously drifts
        const r = d.rot + Math.sin(t * d.spinSpeed + d.phase) * 6;
        const op = fadeChaos * (0.4 + 0.5 * Math.sin(t * 0.7 + d.phase));
        el.style.transform =
          `translate3d(${x}vw, ${y}vh, ${z}px) translate(-50%, -50%) rotate(${r.toFixed(2)}deg) scale(${depthScale.toFixed(3)})`;
        el.style.opacity = Math.max(op, 0).toFixed(3);
      });

      // ---- Focus question typewriter ----
      if (focusRef.current) {
        const appear = ease(sub(p, 0.3, 0.45));
        const disappear = 1 - ease(sub(p, 0.5, 0.62));
        const op = appear * disappear;
        const charCount = Math.floor(appear * FOCUS_TEXT.length);
        focusRef.current.textContent = FOCUS_TEXT.slice(0, charCount);
        focusRef.current.style.opacity = op.toFixed(3);
        if (focusCaretRef.current) {
          focusCaretRef.current.style.opacity =
            (op * (charCount < FOCUS_TEXT.length ? 1 : 0.45 + Math.sin(t * 4) * 0.45)).toFixed(3);
        }
      }

      // ---- Tree assembly ----
      const treeAppear = ease(sub(p, 0.5, 0.78));
      const treeFade = 1 - ease(sub(p, 0.92, 1));
      const treeOp = treeAppear * treeFade;

      // Anchor positions
      const RADIUS_X = 32; // vw
      const RADIUS_Y = 18; // vh
      ANCHORS.forEach((a, i) => {
        const el = anchorRefs.current[i];
        if (!el) return;
        const startX = (i === 0 ? -42 : i === 1 ? 42 : i === 2 ? -32 : 32) + Math.sin(t * 0.3 + i) * 4;
        const startY = (i === 0 ?  22 : i === 1 ? -18 : i === 2 ?  20 : -24) + Math.cos(t * 0.25 + i) * 3;
        const rad = (a.angle * Math.PI) / 180;
        const tx = Math.cos(rad) * RADIUS_X;
        const ty = -Math.sin(rad) * RADIUS_Y;
        const x = startX + (tx - startX) * treeAppear;
        const y = startY + (ty - startY) * treeAppear;
        const op = (0.5 + treeAppear * 0.5) * treeFade;
        // small "land" overshoot
        const overshoot = 1 + Math.sin(treeAppear * Math.PI) * 0.05;
        el.style.transform =
          `translate3d(${x}vw, ${y}vh, 0) translate(-50%, -50%) scale(${overshoot.toFixed(3)})`;
        el.style.opacity = op.toFixed(3);
      });

      // Lines DRAW out from centre (scaleX)
      linesRef.current.forEach((el, i) => {
        if (!el) return;
        const drawProgress = ease(sub(p, 0.55 + i * 0.025, 0.78 + i * 0.025));
        const pulse = 0.7 + 0.3 * Math.sin(t * 1.2 + i * 0.6);
        el.style.transform =
          el.dataset.baseTransform + ` scaleX(${drawProgress.toFixed(3)})`;
        el.style.opacity = (treeOp * 0.95 * pulse).toFixed(3);
      });

      // Shockwave burst at tree formation
      if (burstRef.current) {
        const burstApp = ease(sub(p, 0.55, 0.7));
        const burstFade = 1 - ease(sub(p, 0.66, 0.82));
        const burstOp = burstApp * burstFade;
        const burstScale = 0.2 + burstApp * 8;
        burstRef.current.style.opacity = (burstOp * 0.55).toFixed(3);
        burstRef.current.style.transform =
          `translate(-50%, -50%) scale(${burstScale.toFixed(3)})`;
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [progress, chaos]);

  return (
    <section ref={wrapRef} className="cn" data-testid="cinematic-hero">
      <div className="cn__sticky">
        <div className="cn__nebula" ref={nebulaRef} />
        <div className="cn__rumble" ref={rumbleRef}>
          <div className="cn__scene">
            <div className="cn__stage" ref={stageRef}>
              {/* Chaos word particles */}
              {chaos.map((d, i) => (
                <div
                  key={`w-${i}`}
                  ref={(el) => (wordRefs.current[i] = el)}
                  className={`cn__word${d.accent ? " cn__word--accent" : ""}${d.white ? " cn__word--white" : ""}`}
                  style={{ fontSize: `${d.size}px` }}
                >
                  {d.word}
                </div>
              ))}

              {/* Focus question (typewriter) */}
              <div className="cn__focus" style={{ opacity: 0 }}>
                <span ref={focusRef} className="cn__focus-text" />
                <span ref={focusCaretRef} className="cn__focus-caret" />
              </div>

              {/* Shockwave burst */}
              <div className="cn__burst" ref={burstRef} />

              {/* Tree connector lines (draw from centre via scaleX) */}
              {ANCHORS.map((a, i) => {
                const baseTransform = `translate(0, -50%) rotate(${-a.angle}deg)`;
                return (
                  <div
                    key={`line-${i}`}
                    ref={(el) => (linesRef.current[i] = el)}
                    className="cn__tree-line"
                    data-base-transform={baseTransform}
                    style={{ transform: baseTransform, opacity: 0 }}
                  />
                );
              })}

              {/* Tree node halos */}
              {ANCHORS.map((a, i) => {
                const rad = (a.angle * Math.PI) / 180;
                const tx = Math.cos(rad) * 32;
                const ty = -Math.sin(rad) * 18;
                return (
                  <div
                    key={`halo-${i}`}
                    className="cn__halo"
                    style={{ transform: `translate3d(${tx}vw, ${ty}vh, 0) translate(-50%, -50%)` }}
                  />
                );
              })}

              {/* Tree anchor words */}
              {ANCHORS.map((a, i) => (
                <div
                  key={`a-${i}`}
                  ref={(el) => (anchorRefs.current[i] = el)}
                  className="cn__word cn__word--anchor"
                  style={{ opacity: 0 }}
                >
                  {a.word}
                </div>
              ))}

              {/* Root pulse */}
              <div className="cn__root" />
            </div>
          </div>
        </div>

        <TopBar progress={progress} />
        <ScrollHint progress={progress} />
        <ProgressDots progress={progress} />
        <CtaPanel progress={progress} onCtaClick={() => setGateOpen(true)} />
      </div>
      <LeadGate open={gateOpen} onClose={() => setGateOpen(false)} />
    </section>
  );
}
